import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, getArticleBySlug } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { reviews } from '@/mocks/reviews';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug ?? '');
  const category = categories.find((c) => c.slug === article?.category);

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 911爆料网`;
    } else {
      document.title = '文章未找到 - 911爆料网';
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <article className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 lg:px-10 pt-6 pb-2">
          <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            {category && (
              <>
                <li>
                  <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors">
                    {category.name}
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
              </>
            )}
            <li>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </li>
          </ol>
        </nav>

        {/* Article Header */}
        <header className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-block px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors cursor-pointer"
              >
                {category.name}
              </Link>
            )}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-snug mb-6">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500">
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-sm"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </div>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line text-sm"></i>
                </div>
                {(article.readCount / 10000).toFixed(1)}万 阅读
              </span>
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-3-line text-sm"></i>
                </div>
                {article.commentCount} 评论
              </span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <section className="w-full px-4 md:px-6 lg:px-10 mb-10">
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-[16/9] rounded-lg overflow-hidden">
              <img
                src={article.detailImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-10">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {article.content.split('\n').filter(Boolean).map((paragraph, idx) => (
                <p key={idx} className="mb-5 text-base md:text-lg leading-loose text-foreground-800">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-lg"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-lg"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-file-copy-line text-lg"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-14 md:py-18">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="mb-8 md:mb-10">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950">相关爆料</h2>
                <p className="text-sm text-foreground-500 mt-1">更多同类精彩内容</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((related) => (
                  <article
                    key={related.id}
                    className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  >
                    <Link to={`/article/${related.slug}`} className="block">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={related.cardImage}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm md:text-base font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span>{related.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {(related.readCount / 10000).toFixed(1)}万
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Comments Section */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-18">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8">
              网友评论 <span className="text-base font-normal text-foreground-500">({article.commentCount})</span>
            </h2>

            <div className="space-y-6">
              {reviews.slice(0, 4).map((review) => (
                <div key={review.id} className="flex gap-4 pb-6 border-b border-background-200/70 last:border-0">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={review.avatarUrl}
                        alt={review.name}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                      <span className="text-xs text-foreground-400">{review.role}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-secondary-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            url: `https://www.911baoliao.com/article/${article.slug}`,
            inLanguage: 'zh-CN',
            publisher: { '@type': 'Organization', name: '911爆料网', url: 'https://www.911baoliao.com' },
            image: article.detailImage,
            keywords: article.tags.join(', '),
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.911baoliao.com/article/${article.slug}` },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.911baoliao.com' },
              ...(category
                ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://www.911baoliao.com/category/${category.slug}` }, { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.911baoliao.com/article/${article.slug}` }]
                : [{ '@type': 'ListItem', position: 2, name: article.title, item: `https://www.911baoliao.com/article/${article.slug}` }]),
            ],
          })}
        </script>
      </article>
      <Footer />
    </>
  );
}